// The legacy's navigation plumbing: the `SENDER` round trip, and the worklist
// filter state that rides along with it.
//
// `MainStudyList.aspx.vb:546`, `StudyReport.aspx.vb:371`, `:530`,
// `SendReport.aspx.vb:999`, `StudyInformation.aspx.vb:741` — every deep link in
// CardioReportManager carries the page that opened it:
//
//     StudyReport.aspx  ->  LEAUltraSoundUpdate.aspx?STUDYID=42
//                             &SENDER=StudyReport.aspx?STUDYID=42
//
// and the destination's Save and Cancel both end in
// `Response.Redirect(ViewState("SENDER"))` (`CarotidInterpretationForm.aspx.vb:274,283`).
// That is why the interpretation forms need no navigation bar at all: they are
// modal, and the only way out is back to whoever opened them.
//
// The second thing carried is the query string itself. The legacy kept the
// worklist's search criteria in `Session` (`Session("ActiveSearchCriteria")`);
// here it lives in the URL, so every link between screens has to preserve it or
// the user loses their filters on the way back.

/** The legacy's own parameter name, lower-cased for a query string we author. */
export const SENDER = 'sender'

/**
 * The worklist filter state, carried between screens.
 *
 * `sender` is dropped: it belongs to one hop, and letting it accumulate turns
 * a three-screen round trip into a URL that grows every time.
 */
export function carried(params: URLSearchParams): URLSearchParams {
  const q = new URLSearchParams(params)
  q.delete(SENDER)
  return q
}

/** A link to `path` that keeps the carried filters. */
export function to(path: string, params: URLSearchParams): string {
  const q = carried(params).toString()
  return q ? `${path}?${q}` : path
}

/** A link to `path` that also tells the destination where it was opened from. */
export function toWithSender(
  path: string,
  params: URLSearchParams,
  from: string,
): string {
  const q = carried(params)
  q.set(SENDER, from)
  return `${path}?${q.toString()}`
}

/**
 * Where `Back` goes: the caller named in `?sender=`, or `fallback`.
 *
 * Only same-origin paths are honoured. A `sender` is a URL the user can type,
 * and the legacy fed it straight into `Response.Redirect` — which is how you
 * get an open redirect out of a navigation feature. Anything that is not a
 * single-slash absolute path is ignored, not repaired.
 */
export function senderOf(params: URLSearchParams, fallback: string): string {
  const s = params.get(SENDER)
  if (!s || !s.startsWith('/') || s.startsWith('//')) return fallback
  return s
}

/** True when this page was opened from somewhere that wants the user back. */
export function hasSender(params: URLSearchParams): boolean {
  const s = params.get(SENDER)
  return !!s && s.startsWith('/') && !s.startsWith('//')
}
